export type CaptureStage = 'before' | 'after';
export type ChartShot = { id: string; scope: string; stage: CaptureStage; symbol: string; timeframe: string; source?: string; priceType?: string; capturedAt: number; blob: Blob };

export function shotId(scope: string, stage: CaptureStage) { return scope + ':' + stage; }

function openShots(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('Storage unavailable')); return; }
    const request = indexedDB.open('zonepips-chart-shots', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('shots', { keyPath: 'id' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Storage busy'));
  });
}

export async function readShots(scope: string): Promise<ChartShot[]> {
  const db = await openShots();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('shots', 'readonly');
    const shots: ChartShot[] = [];
    for (const stage of ['before', 'after'] as const) {
      const request = tx.objectStore('shots').get(shotId(scope, stage));
      request.onsuccess = () => { if (request.result?.blob instanceof Blob) shots.push(request.result); };
    }
    tx.oncomplete = () => { db.close(); resolve(shots); };
    tx.onerror = tx.onabort = () => { db.close(); reject(tx.error); };
  });
}

export async function saveShot(shot: ChartShot): Promise<void> {
  const db = await openShots();
  return new Promise((resolve, reject) => {
    // A single keyed record is updated; another pair/slot is never overwritten.
    const tx = db.transaction('shots', 'readwrite');
    tx.objectStore('shots').put(shot);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = tx.onabort = () => { db.close(); reject(tx.error); };
  });
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob), image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Gambar chart tidak dapat dibaca. Cuba semula.')); };
    image.src = url;
  });
}

function png(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Screenshot gagal. Cuba semula.')), 'image/png'));
}

export async function captureChart(svg: SVGSVGElement, meta: Omit<ChartShot, 'id' | 'scope' | 'blob'>): Promise<Blob> {
  // Snapshot synchronously, before a live tick or symbol change can alter it.
  const copy = svg.cloneNode(true) as SVGSVGElement;
  const width = Number(svg.viewBox.baseVal.width), height = Number(svg.viewBox.baseVal.height);
  if (!width || !height) throw new Error('Chart belum tersedia.');
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  copy.setAttribute('width', String(width)); copy.setAttribute('height', String(height));
  copy.setAttribute('style', 'font-family:Consolas,monospace');
  copy.querySelectorAll('[data-testid="chart-cursor"], [data-export-ignore]').forEach(node => node.remove());
  const image = await loadImage(new Blob([new XMLSerializer().serializeToString(copy)], { type: 'image/svg+xml;charset=utf-8' }));
  const ratio = Math.min(2, 2200 / width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * ratio); canvas.height = Math.round((height + 106) * ratio);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Screenshot tidak disokong dalam browser ini.');
  ctx.scale(ratio, ratio);
  ctx.fillStyle = '#101712'; ctx.fillRect(0, 0, width, height + 106);
  ctx.fillStyle = '#edcc86'; ctx.font = 'bold 23px Georgia'; ctx.fillText('ZonePips.', 18, 31);
  ctx.font = 'bold 11px Arial'; ctx.textAlign = 'right'; ctx.fillText(meta.stage.toUpperCase(), width - 18, 29);
  ctx.textAlign = 'left'; ctx.fillStyle = '#c1cabe'; ctx.font = '11px Arial';
  ctx.fillText(meta.symbol + '  /  ' + meta.timeframe + '  ·  ' + (meta.source || 'Dukascopy').toUpperCase() + ' ' + (meta.priceType || 'bid').toUpperCase(), 18, 53);
  ctx.drawImage(image, 0, 68, width, height);
  ctx.fillStyle = '#b1bbaa'; ctx.font = '10px Arial';
  ctx.fillText(new Date(meta.capturedAt).toISOString().replace('T', ' ').slice(0, 19) + ' UTC', 18, height + 91);
  ctx.textAlign = 'right'; ctx.fillStyle = '#edcc86'; ctx.fillText('kakibolamelaka.com', width - 18, height + 91);
  return png(canvas);
}

export async function compareShots(before: ChartShot, after: ChartShot): Promise<Blob> {
  if (before.scope !== after.scope) throw new Error('Pilih Before dan After untuk pair dan timeframe yang sama.');
  const [a, b] = await Promise.all([loadImage(before.blob), loadImage(after.blob)]);
  const panelWidth = Math.min(1100, Math.max(a.width, b.width));
  const aHeight = a.height * panelWidth / a.width, bHeight = b.height * panelWidth / b.width;
  const canvas = document.createElement('canvas'); canvas.width = panelWidth * 2 + 16; canvas.height = Math.ceil(Math.max(aHeight, bHeight));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Screenshot tidak disokong.');
  ctx.fillStyle = '#101712'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(a, 0, 0, panelWidth, aHeight); ctx.drawImage(b, panelWidth + 16, 0, panelWidth, bHeight);
  return png(canvas);
}

export function downloadShot(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob), link = document.createElement('a');
  link.href = url; link.download = filename; document.body.append(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
